import { useEffect } from 'react'

/**
 * Must match <title> in index.html. Restoring to a constant rather than to
 * whatever document.title happened to be at mount keeps two callers from
 * nesting into a stale value.
 */
const DEFAULT_TITLE = 'ibibik'

/**
 * Sets the tab title while the caller is mounted and restores the default on
 * unmount. Pass null to leave the title alone — a page whose data has not
 * loaded yet has nothing honest to put there.
 *
 * On the og: tags in index.html: those are deliberately generic because
 * recipient links get pasted into chat apps that unfurl them, and a dropbox's
 * title would then be shown to everyone in the channel. That constraint is
 * about the *static* document. Unfurlers fetch the URL server-side and parse
 * the HTML they get; they do not run the SPA, so a title written here reaches
 * neither the unfurl card nor a crawler, and the meta block stays untouched.
 *
 * Which is why both token routes may use this. The only audience for a title
 * set from JavaScript is whoever already has the page open — someone who is
 * looking at the same string in the <h1>. Writing og:title from JS, by
 * contrast, would be the change that breaks the rule: it buys nothing, since
 * no unfurler reads it, and it puts the title into the one element that exists
 * to be scraped.
 *
 * The restore is not optional. Nothing reloads in a single-page app, so without
 * it going back to the dashboard would leave a customer's dropbox title sitting
 * in the tab, the window switcher and any screen share.
 */
export function useDocumentTitle(title: string | null) {
  useEffect(() => {
    if (title === null) return
    document.title = title
    return () => {
      document.title = DEFAULT_TITLE
    }
  }, [title])
}
